import { CheckCircle, AlertTriangle, XCircle, Users, Building2, Target, User } from 'lucide-react'
import type { OrganizationUpload } from '@/types'

interface ImportReportCardProps {
    upload: OrganizationUpload
}

export default function ImportReportCard({ upload }: ImportReportCardProps) {
    const report = upload.import_report

    if (!report) {
        return (
            <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Import Details</h3>
                </div>
                <div className="p-6">
                    <p className="text-sm text-gray-500">No import report available</p>
                </div>
            </div>
        )
    }

    const formatDuration = (seconds: number) => {
        if (seconds < 1) return `${(seconds * 1000).toFixed(0)}ms`
        return `${seconds.toFixed(2)}s`
    }

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'success':
                return <CheckCircle className="w-5 h-5 text-green-600" />
            case 'completed_with_warnings':
                return <AlertTriangle className="w-5 h-5 text-yellow-600" />
            case 'completed_with_errors':
                return <XCircle className="w-5 h-5 text-red-600" />
            default:
                return <CheckCircle className="w-5 h-5 text-gray-400" />
        }
    }

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'success':
                return 'text-green-600'
            case 'completed_with_warnings':
                return 'text-yellow-600'
            case 'completed_with_errors':
                return 'text-red-600'
            default:
                return 'text-gray-600'
        }
    }

    return (
        <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Import Report</h3>
                    <div className="flex items-center space-x-2">
                        {getStatusIcon(report.status)}
                        <span className={`text-sm font-medium capitalize ${getStatusColor(report.status)}`}>
                            {report.status?.replace(/_/g, ' ')}
                        </span>
                    </div>
                </div>
            </div>

            <div className="p-6 space-y-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {report.organization && (
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                            <Building2 className="w-6 h-6 text-blue-600 mx-auto mb-1" />
                            <div className="text-sm font-medium text-gray-900">1</div>
                            <div className="text-xs text-gray-500">Organization</div>
                        </div>
                    )}
                    
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                        <Building2 className="w-6 h-6 text-green-600 mx-auto mb-1" />
                        <div className="text-sm font-medium text-gray-900">{report.accounts?.length || 0}</div>
                        <div className="text-xs text-gray-500">Accounts</div>
                    </div>
                    
                    <div className="text-center p-3 bg-purple-50 rounded-lg">
                        <Target className="w-6 h-6 text-purple-600 mx-auto mb-1" />
                        <div className="text-sm font-medium text-gray-900">{report.opportunities?.length || 0}</div>
                        <div className="text-xs text-gray-500">Opportunities</div>
                    </div>
                    
                    <div className="text-center p-3 bg-indigo-50 rounded-lg">
                        <Users className="w-6 h-6 text-indigo-600 mx-auto mb-1" />
                        <div className="text-sm font-medium text-gray-900">{report.users?.length || 0}</div>
                        <div className="text-xs text-gray-500">Users</div>
                    </div>
                </div>

                {/* Organization Details */}
                {report.organization && (
                    <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Organization</h4>
                        <div className="bg-gray-50 rounded-lg p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <span className="text-sm font-medium text-gray-500">Name:</span>
                                    <span className="ml-2 text-sm text-gray-900">{report.organization.name}</span>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-500">Slug:</span>
                                    <span className="ml-2 text-sm text-gray-900">{report.organization.slug}</span>
                                </div>
                                {report.organization.website && (
                                    <div>
                                        <span className="text-sm font-medium text-gray-500">Website:</span>
                                        <span className="ml-2 text-sm text-gray-900">{report.organization.website}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Accounts */}
                {report.accounts && report.accounts.length > 0 && (
                    <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Accounts Created</h4>
                        <div className="space-y-2">
                            {report.accounts.map((account: any, index: number) => (
                                <div key={index} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                                    <div className="flex items-center space-x-3">
                                        <Building2 className="w-4 h-4 text-gray-400" />
                                        <div>
                                            <span className="text-sm font-medium text-gray-900">{account.name}</span>
                                            {account.industry && (
                                                <span className="ml-2 text-xs text-gray-500">({account.industry})</span>
                                            )}
                                        </div>
                                    </div>
                                    {account.opportunities_count > 0 && (
                                        <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                                            {account.opportunities_count} opportunities
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Errors */}
                {report.errors && report.errors.length > 0 && (
                    <div>
                        <h4 className="text-sm font-medium text-red-900 mb-3">Errors ({report.errors.length})</h4>
                        <div className="space-y-2">
                            {report.errors.map((error: any, index: number) => (
                                <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-3">
                                    <div className="flex items-start">
                                        <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                                        <div className="ml-2">
                                            <p className="text-sm text-red-800">{error.message}</p>
                                            {error.entity_type && (
                                                <p className="text-xs text-red-600 mt-1">
                                                    {error.entity_type} • {new Date(error.timestamp).toLocaleString()}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Warnings */}
                {report.warnings && report.warnings.length > 0 && (
                    <div>
                        <h4 className="text-sm font-medium text-yellow-900 mb-3">Warnings ({report.warnings.length})</h4>
                        <div className="space-y-2">
                            {report.warnings.map((warning: any, index: number) => (
                                <div key={index} className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                                    <div className="flex items-start">
                                        <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                                        <div className="ml-2">
                                            <p className="text-sm text-yellow-800">{warning.message}</p>
                                            {warning.entity_type && (
                                                <p className="text-xs text-yellow-600 mt-1">
                                                    {warning.entity_type} • {new Date(warning.timestamp).toLocaleString()}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Timing */}
                <div className="pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-500">
                        <div>
                            <span className="font-medium">Mode:</span>
                            <span className="ml-1 capitalize">{report.mode}</span>
                        </div>
                        <div>
                            <span className="font-medium">Duration:</span>
                            <span className="ml-1">
                                {report.duration_seconds ? formatDuration(report.duration_seconds) : 'N/A'}
                            </span>
                        </div>
                        <div>
                            <span className="font-medium">Started:</span>
                            <span className="ml-1">
                                {report.started_at ? new Date(report.started_at).toLocaleTimeString() : 'N/A'}
                            </span>
                        </div>
                        <div>
                            <span className="font-medium">Completed:</span>
                            <span className="ml-1">
                                {report.completed_at ? new Date(report.completed_at).toLocaleTimeString() : 'N/A'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}